import { decodeHtmlEntities } from './decodeHtmlEntities';
import {
  isInlineMathLike,
  isSpreadsheetAbsoluteReference,
} from './isInlineMathLike';

export type MathSegment =
  | { type: 'text'; content: string }
  | { type: 'math'; content: string; display: boolean };

export type MathContentRange = {
  start: number;
  end: number;
  content: string;
  display: boolean;
};

const ZWSP = '\u200B';

const isEscapedDollar = (content: string, index: number): boolean => {
  let slashCount = 0;

  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    if (content[cursor] !== '\\') break;
    slashCount += 1;
  }

  return slashCount % 2 === 1;
};

const findClosingDollar = (content: string, start: number): number => {
  for (let index = start; index < content.length; index += 1) {
    if (content[index] === '$' && !isEscapedDollar(content, index)) {
      return index;
    }
  }

  return -1;
};

const normalizeZwspMathContent = (
  content: string,
): Pick<MathContentRange, 'content' | 'display'> => {
  if (content.startsWith('$$') && content.endsWith('$$')) {
    return {
      content: content.slice(2, -2),
      display: true,
    };
  }

  if (content.startsWith('$') && content.endsWith('$')) {
    return {
      content: content.slice(1, -1),
      display: false,
    };
  }

  return {
    content,
    display: false,
  };
};

export const findMathContentRanges = (content: string): MathContentRange[] => {
  const ranges: MathContentRange[] = [];

  for (let index = 0; index < content.length; index += 1) {
    if (content[index] === ZWSP) {
      const end = content.indexOf(ZWSP, index + 1);

      if (end > index + 1) {
        ranges.push({
          start: index,
          end: end + 1,
          ...normalizeZwspMathContent(content.slice(index + 1, end)),
        });
        index = end;
      }

      continue;
    }

    if (content.startsWith('$$', index) && !isEscapedDollar(content, index)) {
      const end = content.indexOf('$$', index + 2);

      if (end >= index + 2) {
        ranges.push({
          start: index,
          end: end + 2,
          content: content.slice(index + 2, end),
          display: true,
        });
        index = end + 1;
      }

      continue;
    }

    if (content[index] !== '$' || isEscapedDollar(content, index)) {
      continue;
    }

    if (/^\s/.test(content[index + 1] ?? '')) {
      continue;
    }

    const end = findClosingDollar(content, index + 1);
    if (end === -1) continue;

    const inlineContent = content.slice(index + 1, end);
    // 렌더러는 sanitize 한 문자열을 넘기므로 엔티티를 풀어 평문으로 판정한다.
    if (!isInlineMathLike(decodeHtmlEntities(inlineContent))) {
      // 내용 때문에 거부한 쌍의 닫는 `$` 를 다음 여는 구분자로 재사용하지 않는다
      // (`$한글$+$x$` 에서 `$+$` 가 수식이 됨). 닫는 `$` 앞이 공백이면 원래 여는
      // 구분자였을 가능성이 커서(`$5, 식 $x^2$`) 재사용한다.
      if (!/\s$/.test(inlineContent)) index = end;
      continue;
    }
    if (isSpreadsheetAbsoluteReference(inlineContent, content[end + 1])) {
      // 닫는 `$` 까지 건너뛴다 — 안 그러면 `$E$4*$F$2` 의 `$4*$` 가 수식이 된다.
      index = end;
      continue;
    }

    ranges.push({
      start: index,
      end: end + 1,
      content: inlineContent,
      display: false,
    });
    index = end;
  }

  return ranges;
};

/**
 * Parse text content to extract math expressions and plain text segments.
 * Handles ZWSP (Zero-Width Space, \u200B) as delimiter around math expressions.
 *
 * Supported patterns:
 * - ZWSP-delimited: \u200B...\u200B (anything wrapped in ZWSP is treated as math)
 * - Display Math: $$...$$
 * - Inline Math: $...$
 * - Escaped Dollar: \$ is preserved as plain text
 *
 * @param content - The text content to parse (may contain ZWSP and LaTeX)
 * @returns Array of segments with type 'text' or 'math'
 */
export const parseMathContent = (content: string): MathSegment[] => {
  const ranges = findMathContentRanges(content);
  const segments: MathSegment[] = [];
  let cursor = 0;

  for (const range of ranges) {
    if (range.start > cursor) {
      segments.push({
        type: 'text',
        content: content.slice(cursor, range.start),
      });
    }

    segments.push({
      type: 'math',
      content: range.content,
      display: range.display,
    });
    cursor = range.end;
  }

  if (cursor < content.length) {
    segments.push({ type: 'text', content: content.slice(cursor) });
  }

  return segments;
};
